import { z } from 'zod'

/**
 * Auth and org form schemas — the single definition both platforms validate
 * with (react-hook-form on the client, and the server re-validates through its
 * own TypeBox route schemas; better-auth applies its own rules on top).
 *
 * Messages are written for form fields, not for API errors.
 */

export const emailSchema = z.email('Enter a valid email address').max(320)

/**
 * better-auth's default minimum is 8; the max guards against DoS-sized
 * payloads reaching the scrypt hasher.
 */
export const passwordSchema = z
  .string()
  .min(8, 'Password must be at least 8 characters')
  .max(128, 'Password must be at most 128 characters')

export const signInSchema = z.object({
  email: emailSchema,
  password: z.string().min(1, 'Enter your password'),
})
export type SignInInput = z.infer<typeof signInSchema>

export const signUpSchema = z.object({
  name: z.string().trim().min(1, 'Enter your name').max(200),
  email: emailSchema,
  password: passwordSchema,
})
export type SignUpInput = z.infer<typeof signUpSchema>

export const forgotPasswordSchema = z.object({ email: emailSchema })
export type ForgotPasswordInput = z.infer<typeof forgotPasswordSchema>

export const resetPasswordSchema = z
  .object({
    password: passwordSchema,
    confirmPassword: z.string(),
  })
  .refine((v) => v.password === v.confirmPassword, {
    error: 'Passwords do not match',
    path: ['confirmPassword'],
  })
export type ResetPasswordInput = z.infer<typeof resetPasswordSchema>

export const magicLinkSchema = z.object({ email: emailSchema })
export type MagicLinkInput = z.infer<typeof magicLinkSchema>
